"use client"

import { useState, useEffect } from "react"
import { Copy, Crown, Star, TrendingUp, Eye, EyeOff, ArrowUpRight, Sparkles, Diamond, Zap, Users2, Activity, Wallet, Gift, Award, Shield, Target } from "lucide-react"
import { Tooltip, CircularProgress, Avatar, Chip, Snackbar, Alert } from "@mui/material"
import { useSelector, useDispatch } from "react-redux"
import { userSelect, homeScreenStatSelect, allRemoteSettingsSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import { capitalizeFirstLetter, niceDate, money } from "../../../utility/Helper1"
import WrapUpP2p from "../p2p/layouts/WrapUpP2p"
import { useNavigate } from "react-router-dom"

export default function AffiliateDashboard() {
  const dispatch = useDispatch()
  const site_settings = useSelector(allRemoteSettingsSelect)
  const userData = useSelector(userSelect)
  const finance = useSelector(homeScreenStatSelect)
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState(true)
  const [showUpline, setShowUpline] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")
  const [viewingLevel, setViewingLevel] = useState(1)
  const [refData, setRefData] = useState([])
  const [refDataRaw, setRefDataRaw] = useState([])
  const [mainUplineData, setMainUplineData] = useState({})
  const [lengthToShow, setLengthToShow] = useState(20)
  const [activeSection, setActiveSection] = useState("overview")
  const [hideBalance, setHideBalance] = useState(false)

  const myRefCode = userData?.myrefid
  const myRefLink = window.location.origin + "/#/signup?ref=" + myRefCode

  // Guider data from API
  const isGuider = userData?.guider?.isAGuider == 1
  const guiderSettings = userData?.guider?.guider_setting || []

  const loadDownLines = (myrefid) => {
    setIsLoading(true)
    dispatch(
      authActions.onRefreshSystemData(`user/referral/${myrefid}`, 10, {}, (response) => {
        setIsLoading(false)
        setRefDataRaw(response?.data)
        setRefData(response?.data?.referrals)
        setMainUplineData(response?.data?.upline)
      })
    )
  }

  useEffect(() => {
    loadDownLines(userData?.myrefid)
  }, [userData?.myrefid])

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(myRefLink)
      .then(() => {
        setSnackbarMessage("Elite referral code copied!")
        setSnackbarSeverity("success")
        setSnackbarOpen(true)
      })
      .catch(() => {
        setSnackbarMessage("Failed to copy referral code")
        setSnackbarSeverity("error")
        setSnackbarOpen(true)
      })
  }

  const showMore = () => {
    setLengthToShow((el) => el + 20)
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  // Get data for the current viewing level
  const currentLevelData = refData?.["level" + viewingLevel] || []
  const allReferralData = currentLevelData?.slice(0, lengthToShow)

  // Calculate total counts for each level
  const level1Count = refData?.level1?.length || 0
  const level2Count = refData?.level2?.length || 0
  const level3Count = refData?.level3?.length || 0

  // Calculate total earnings
  const commissionAmount = finance?.[0]?.commission ?? 0
  const hasCommission = commissionAmount > 0
  const currencyType = finance?.[0]?.type

  // Get current user tier
  const getCurrentTier = () => {
    if (!isGuider || !guiderSettings.length) return null
    
    // Sort tiers by invites descending to find the highest tier user qualifies for
    const sortedTiers = [...guiderSettings].sort((a, b) => b.invites - a.invites)
    return sortedTiers.find(tier => level1Count >= tier.invites) || null
  }

  const currentTier = getCurrentTier()

  // Calculate guider earnings
  const calculateGuiderEarnings = () => {
    if (!isGuider || !currentTier) return 0
    
    const earnings = level1Count * currentTier.bonus
    return currentTier.use_percentage == 1 ? earnings : earnings
  }

  const guiderEarnings = calculateGuiderEarnings()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black">
        <div className="text-center">
          <CircularProgress sx={{ color: '#fbbf24' }} size={60} />
          <p className="text-yellow-400 mt-4 font-medium">Loading your empire...</p>
        </div>
      </div>
    )
  }

  const navigationTabs = [
    { id: "overview", label: "Overview", icon: Crown },
    { id: "guider", label: "Guider Profile", icon: Shield },
    { id: "network", label: "Network", icon: Diamond },
    { id: "vault", label: "Vault", icon: Wallet },
    { id: "recruiting", label: "Recruit", icon: Gift }
  ]

  return (
    <WrapUpP2p allowAutoPadding={false}>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-slate-800/90 to-gray-900/90 backdrop-blur-xl border-b border-yellow-400/20">
          <div className="px-4 lg:px-8 py-6">
            {/* Profile Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Avatar
                    src={userData?.avatar}
                    alt={userData?.username}
                    sx={{
                      width: { xs: 56, lg: 72 },
                      height: { xs: 56, lg: 72 },
                      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                      boxShadow: '0 8px 32px rgba(251, 191, 36, 0.3)',
                    }}
                  />
                  <div className="absolute -top-1 -right-1 lg:-top-2 lg:-right-2 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full p-1.5 lg:p-2">
                    {isGuider ? (
                      <Shield className="h-3 w-3 lg:h-4 lg:w-4 text-gray-900" />
                    ) : (
                      <Crown className="h-3 w-3 lg:h-4 lg:w-4 text-gray-900" />
                    )}
                  </div>
                </div>
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-white">
                    {capitalizeFirstLetter(userData?.username)}
                  </h1>
                  <p className="text-yellow-400 text-sm lg:text-base font-medium">
                    {isGuider ? `${currentTier?.name} Guider` : "Elite Ambassador"}
                  </p>
                  <div className="flex items-center mt-1 text-xs text-gray-400">
                    <Sparkles className="h-3 w-3 mr-1" />
                    ID: {myRefCode}
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="hidden sm:flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-yellow-400">
                    {level1Count + level2Count + level3Count}
                  </div>
                  <div className="text-xs text-gray-400">Total Empire</div>
                </div>
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-green-400">
                    {hideBalance ? "••••" : money(commissionAmount, currencyType, { removeSymbol: true })}
                  </div>
                  <div className="text-xs text-gray-400">Total Balance</div>
                </div>
              </div>
            </div>

            {/* Guider Status Banner */}
            {isGuider && currentTier && (
              <div className="mb-6 bg-gradient-to-r from-green-400/10 to-emerald-500/10 border border-green-400/20 rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-lg p-2">
                      <Award className="h-5 w-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">🎉 {currentTier.name} Guider Status Active!</h3>
                      <p className="text-sm text-gray-300">
                        Additional {currentTier.use_percentage == 1 ? `${currentTier.bonus}%` : `$${currentTier.bonus}`} per referral • {level1Count} active referrals
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-400">
                      {currentTier.use_percentage == 1 ? `${guiderEarnings}%` : `$${guiderEarnings}`}
                    </div>
                    <div className="text-xs text-gray-400">Guider Earnings</div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="flex space-x-1 bg-gray-900/50 rounded-2xl p-2 overflow-x-auto">
              {navigationTabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSection(tab.id)}
                    className={`flex items-center px-4 lg:px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                      activeSection == tab.id
                        ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 shadow-lg shadow-yellow-400/25"
                        : "text-gray-300 hover:text-yellow-300 hover:bg-gray-800/50"
                    }`}
                  >
                    <Icon className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                    <span className="text-sm lg:text-base">{tab.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 lg:p-8">
          {/* Overview Section */}
          {activeSection == "overview" && (
            <div className="space-y-6 lg:space-y-8">
              <div className="text-center lg:text-left">
                <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent mb-2">
                  Empire Dashboard
                </h2>
                <p className="text-gray-400">Command your affiliate kingdom</p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                <div className="relative overflow-hidden bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-4 lg:p-6">
                  <div className="absolute top-0 right-0 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-bl from-yellow-400/10 to-transparent rounded-full blur-xl"></div>
                  <div className="relative">
                    <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3">
                      <Crown className="h-4 w-4 lg:h-5 lg:w-5 text-yellow-400" />
                    </div>
                    <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      {level1Count + level2Count + level3Count}
                    </div>
                    <div className="text-yellow-400 font-medium text-xs lg:text-sm">Total Empire</div>
                  </div>
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-4 lg:p-6">
                  <div className="absolute top-0 right-0 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-bl from-amber-400/10 to-transparent rounded-full blur-xl"></div>
                  <div className="relative">
                    <div className="bg-gradient-to-r from-amber-400/20 to-orange-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3">
                      <Star className="h-4 w-4 lg:h-5 lg:w-5 text-amber-400" />
                    </div>
                    <div className="text-2xl lg:text-3xl font-bold text-white mb-1">{level1Count}</div>
                    <div className="text-amber-400 font-medium text-xs lg:text-sm">Elite Circle</div>
                  </div>
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-4 lg:p-6">
                  <div className="absolute top-0 right-0 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-bl from-purple-400/10 to-transparent rounded-full blur-xl"></div>
                  <div className="relative">
                    <div className="bg-gradient-to-r from-purple-400/20 to-pink-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3">
                      <Diamond className="h-4 w-4 lg:h-5 lg:w-5 text-purple-400" />
                    </div>
                    <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      {level2Count + level3Count}
                    </div>
                    <div className="text-purple-400 font-medium text-xs lg:text-sm">Extended Network</div>
                  </div>
                </div>

                <div className="relative overflow-hidden bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-4 lg:p-6">
                  <div className="absolute top-0 right-0 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-bl from-green-400/10 to-transparent rounded-full blur-xl"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <div className="bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-lg p-2 lg:p-3">
                        <Wallet className="h-4 w-4 lg:h-5 lg:w-5 text-green-400" />
                      </div>
                      <button
                        onClick={() => setHideBalance(!hideBalance)}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                      >
                        {hideBalance ? <EyeOff className="h-3 w-3 lg:h-4 lg:w-4" /> : <Eye className="h-3 w-3 lg:h-4 lg:w-4" />}
                      </button>
                    </div>
                    <div className="text-2xl lg:text-3xl font-bold text-white mb-1">
                      {hideBalance ? "••••••" : money(commissionAmount, currencyType, { removeSymbol: true })}
                    </div>
                    <div className="text-green-400 font-medium text-xs lg:text-sm">Golden Vault</div>
                  </div>
                </div>
              </div>

              {/* Performance Analytics */}
              <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 space-y-4 sm:space-y-0">
                  <h3 className="text-lg lg:text-xl font-bold text-white">Empire Performance</h3>
                  <div className="flex items-center space-x-2">
                    <Chip
                      label="Live Analytics"
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        color: '#1f2937',
                        fontWeight: 'bold'
                      }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 mb-6">
                  <div className="bg-gradient-to-r from-blue-400/10 to-cyan-500/10 border border-blue-400/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-blue-400">{level1Count}</div>
                        <div className="text-gray-300 text-sm">Direct Recruits</div>
                      </div>
                      <TrendingUp className="h-8 w-8 text-blue-400" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-purple-400/10 to-pink-500/10 border border-purple-400/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-purple-400">{level2Count + level3Count}</div>
                        <div className="text-gray-300 text-sm">Network Growth</div>
                      </div>
                      <Users2 className="h-8 w-8 text-purple-400" />
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-green-400/10 to-emerald-500/10 border border-green-400/20 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-green-400">
                          {money(commissionAmount, currencyType, { removeSymbol: true })}
                        </div>
                        <div className="text-gray-300 text-sm">Total Earnings</div>
                      </div>
                      <Activity className="h-8 w-8 text-green-400" />
                    </div>
                  </div>
                </div>

                <div className="h-48 lg:h-64 flex items-center justify-center bg-gray-900/30 rounded-xl border border-gray-700/50">
                  <div className="text-center">
                    <TrendingUp className="h-12 w-12 lg:h-16 lg:w-16 mx-auto mb-4 text-yellow-400/30" />
                    <p className="text-gray-400 text-sm lg:text-base">Advanced analytics coming soon</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Guider Section */}
          {activeSection == "guider" && (
            <div className="space-y-6 lg:space-y-8">
              <div className="text-center lg:text-left">
                <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent mb-2">
                  Guider Profile
                </h2>
                <p className="text-gray-400">Your referral earning status</p>
              </div>

              {/* Guider Status */}
              <div className={`bg-gradient-to-br ${
                isGuider 
                  ? 'from-green-400/10 to-emerald-500/10 border-green-400/20' 
                  : 'from-gray-400/10 to-slate-500/10 border-gray-400/20'
              } backdrop-blur-xl border rounded-2xl p-6 lg:p-8`}>
                <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-6 lg:space-y-0">
                  <div className="flex items-center space-x-4">
                    <div className={`bg-gradient-to-r ${
                      isGuider ? 'from-green-400/20 to-emerald-500/20' : 'from-gray-400/20 to-slate-500/20'
                    } rounded-full p-4`}>
                      {isGuider ? (
                        <Shield className="h-8 w-8 text-green-400" />
                      ) : (
                        <Target className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl lg:text-2xl font-bold text-white">
                        {isGuider ? `${currentTier?.name} Guider` : "Not a Guider"}
                      </h3>
                      <p className="text-gray-300">
                        {isGuider && currentTier
                          ? `Additional ${currentTier.use_percentage == 1 ? `${currentTier.bonus}%` : `$${currentTier.bonus}`} per referral`
                          : "Become a guider to earn additional referral bonuses"}
                      </p>
                    </div>
                  </div>
                  
                  {isGuider && (
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-400">
                        {currentTier?.use_percentage == 1 ? `${guiderEarnings}%` : `$${guiderEarnings}`}
                      </div>
                      <div className="text-gray-400 text-sm">Guider Earnings</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Available Tiers */}
              <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-6 lg:p-8">
                <h3 className="text-lg lg:text-xl font-bold text-white mb-6">Available Guider Tiers</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
                  {guiderSettings.map((tier, index) => {
                    const isCurrentTier = isGuider && currentTier?.name == tier.name
                    const hasEnoughReferrals = level1Count >= tier.invites
                    
                    return (
                      <div key={index} className={`relative overflow-hidden bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border ${
                        isCurrentTier ? 'border-green-400/40' : 
                        hasEnoughReferrals ? 'border-yellow-400/20' : 
                        'border-gray-700/50'
                      } rounded-2xl p-4 lg:p-6`}>
                        <div className="absolute top-0 right-0 w-16 h-16 lg:w-24 lg:h-24 bg-gradient-to-bl from-yellow-400/10 to-transparent rounded-full blur-xl"></div>
                        <div className="relative">
                          <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3">
                            <Award className="h-4 w-4 lg:h-5 lg:w-5 text-yellow-400" />
                          </div>
                          <div className="text-lg lg:text-xl font-bold text-white mb-1">
                            {tier.name}
                          </div>
                          <div className="text-xs lg:text-sm text-gray-400 mb-2">
                            {tier.invites} referrals required
                          </div>
                          <div className="text-sm lg:text-base text-gray-300 mb-3">
                            Additional {tier.use_percentage == 1 ? `${tier.bonus}%` : `$${tier.bonus}`} per referral
                          </div>
                          <div className="flex items-center space-x-2">
                            {isCurrentTier && (
                              <span className="text-xs bg-green-400/20 text-green-400 px-2 py-1 rounded-full">
                                CURRENT
                              </span>
                            )}
                            {hasEnoughReferrals && !isCurrentTier && (
                              <span className="text-xs bg-yellow-400/20 text-yellow-400 px-2 py-1 rounded-full">
                                QUALIFIED
                              </span>
                            )}
                            {!hasEnoughReferrals && (
                              <span className="text-xs bg-gray-400/20 text-gray-400 px-2 py-1 rounded-full">
                                {tier.invites - level1Count} MORE NEEDED
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Network Section */}
          {activeSection == "network" && (
            <div className="space-y-6 lg:space-y-8">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  Network Tiers
                </h2>
                <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                  {[1, 2, 3].map((level) => (
                    <button
                      key={level}
                      onClick={() => setViewingLevel(level)}
                      className={`relative px-4 lg:px-6 py-2 lg:py-3 rounded-xl font-bold transition-all duration-300 text-sm lg:text-base ${
                        viewingLevel == level
                          ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 shadow-lg shadow-yellow-400/25"
                          : "bg-slate-800/50 text-gray-300 hover:bg-slate-700/50 border border-gray-600"
                      }`}
                    >
                      Tier {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Network Members */}
              {allReferralData.length > 0 ? (
                <div className="space-y-3 lg:space-y-4">
                  {allReferralData.map((downline, index) => {
                    const refWithdrawnFromMe = downline?.[`refWithdrawnFromMe_${viewingLevel}`] ?? 0
                    const rebWithdrawnFromMe = downline?.[`rebateWithdrawnFromMe_${viewingLevel}`] ?? 0
                    const totalEarning = refWithdrawnFromMe + rebWithdrawnFromMe

                    return (
                      <div
                        key={downline.id}
                        className="bg-gradient-to-r from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-gray-700/50 rounded-xl p-4 lg:p-6 hover:border-yellow-400/30 transition-all duration-300"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                          <div className="flex items-center space-x-3 lg:space-x-4">
                            <div className="relative flex-shrink-0">
                              <Avatar
                                src={downline?.avatar}
                                alt={downline?.username}
                                sx={{
                                  width: { xs: 48, lg: 56 },
                                  height: { xs: 48, lg: 56 },
                                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                                  boxShadow: '0 4px 16px rgba(251, 191, 36, 0.2)',
                                }}
                              />
                              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full w-3 h-3 lg:w-4 lg:h-4 border-2 border-gray-900"></div>
                            </div>
                            <div>
                              <h4 className="text-base lg:text-lg font-bold text-white">
                                {capitalizeFirstLetter(downline?.username)}
                              </h4>
                              <div className="flex items-center text-xs lg:text-sm text-gray-400">
                                <Activity className="h-3 w-3 mr-1" />
                                Joined {niceDate(downline?.date)}
                              </div>
                            </div>
                          </div>
                          <div className="text-left sm:text-right">
                            <div className="text-xl lg:text-2xl font-bold text-yellow-400">
                              {money(totalEarning, finance?.[0]?.type)}
                            </div>
                            <div className="text-xs lg:text-sm text-gray-400">Total Rewards</div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12 lg:py-16 bg-gradient-to-br from-slate-800/30 to-gray-900/30 border border-gray-700/50 rounded-2xl">
                  <div className="bg-gradient-to-r from-gray-700/50 to-gray-600/50 rounded-full w-16 h-16 lg:w-24 lg:h-24 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                    <Users2 className="h-8 w-8 lg:h-12 lg:w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg lg:text-xl font-bold text-white mb-2">No members in this tier</h3>
                  <p className="text-gray-400 max-w-md mx-auto text-sm lg:text-base px-4">
                    Start building your network to see members appear in this tier.
                  </p>
                </div>
              )}

              {currentLevelData.length > lengthToShow && (
                <div className="text-center">
                  <button
                    onClick={showMore}
                    className="px-6 lg:px-8 py-2 lg:py-3 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 rounded-xl font-bold hover:shadow-lg hover:shadow-yellow-400/25 transition-all duration-300 text-sm lg:text-base"
                  >
                    Load More Members
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Vault Section */}
          {activeSection == "vault" && (
            <div className="space-y-6 lg:space-y-8">
              <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                Golden Vault
              </h2>

              <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-6 lg:p-8">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-6 lg:space-y-0 lg:mb-8">
                  <div>
                    <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">Total Available Balance</h3>
                    <div className="text-3xl lg:text-5xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                      {money(commissionAmount, currencyType, { removeSymbol: false })}
                    </div>
                    <div className="mt-2 text-sm text-gray-400">
                      Commission: {money(commissionAmount, currencyType)} 
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 rounded-xl p-4 lg:p-6 self-center">
                    <Wallet className="h-12 w-12 lg:h-16 lg:w-16 text-yellow-400" />
                  </div>
                </div>

                {(hasCommission) ? (
                  <button
                    onClick={() => {
                     navigate('/withdraw/earning?commission=true')
                    }}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 rounded-xl font-bold hover:shadow-lg hover:shadow-yellow-400/25 transition-all duration-300 group text-sm lg:text-base"
                  >
                    <Zap className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                    Initiate Withdrawal
                    <ArrowUpRight className="h-4 w-4 lg:h-5 lg:w-5 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>
                ) : (
                  <button
                    disabled
                    className="w-full sm:w-auto inline-flex items-center justify-center px-6 lg:px-8 py-3 lg:py-4 bg-gray-700/50 text-gray-400 rounded-xl font-bold cursor-not-allowed text-sm lg:text-base"
                  >
                    <Wallet className="h-4 w-4 lg:h-5 lg:w-5 mr-2" />
                    Insufficient Balance
                  </button>
                )}
              </div>

              {/* Earnings Breakdown */}
              <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-6 lg:p-8">
                <h3 className="text-lg lg:text-xl font-bold text-white mb-4 lg:mb-6">Earnings Breakdown</h3>
                <div className="space-y-3 lg:space-y-4">
                  <div className="flex items-center justify-between p-3 lg:p-4 bg-gradient-to-r from-green-400/10 to-emerald-500/10 rounded-lg border border-green-400/20">
                    <span className="text-gray-300 text-sm lg:text-base">Commission Earnings</span>
                    <span className="text-green-400 font-bold text-sm lg:text-base">{money(commissionAmount, currencyType)}</span>
                  </div>
                  {isGuider && currentTier && (
                    <div className="flex items-center justify-between p-3 lg:p-4 bg-gradient-to-r from-yellow-400/10 to-amber-500/10 rounded-lg border border-yellow-400/20">
                      <span className="text-gray-300 text-sm lg:text-base">
                        Guider Earnings ({currentTier.name})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Recruiting Section */}
          {activeSection == "recruiting" && (
            <div className="space-y-6 lg:space-y-8">
              <h2 className="text-2xl lg:text-4xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                Elite Recruiting
              </h2>

              <div className="bg-gradient-to-br from-slate-800/50 to-gray-900/50 backdrop-blur-xl border border-yellow-400/20 rounded-2xl p-6 lg:p-8">
                <h3 className="text-xl lg:text-2xl font-bold text-white mb-4 lg:mb-6">Your Elite Recruitment Code</h3>
                <div className="bg-gradient-to-r from-yellow-400/10 to-amber-500/10 border border-yellow-400/30 rounded-xl p-4 lg:p-6 mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
                    <div className="font-mono text-sm lg:text-lg text-yellow-400 bg-gray-900/50 px-3 lg:px-4 py-2 rounded-lg break-all">
                      {myRefLink}
                    </div>
                    <Tooltip title="Copy elite code">
                      <button
                        onClick={copyToClipboard}
                        className="bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900 p-3 rounded-lg hover:shadow-lg transition-all duration-300 self-center sm:self-auto flex-shrink-0"
                      >
                        <Copy className="h-5 w-5" />
                      </button>
                    </Tooltip>
                  </div>
                </div>

                {/* Strategy Guide */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
                  <div className="bg-gradient-to-br from-purple-400/10 to-pink-500/10 border border-purple-400/20 rounded-xl p-4 lg:p-6">
                    <div className="bg-gradient-to-r from-purple-400/20 to-pink-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3 lg:mb-4">
                      <Gift className="h-5 w-5 lg:h-6 lg:w-6 text-purple-400" />
                    </div>
                    <h4 className="text-base lg:text-lg font-bold text-white mb-2">Share & Conquer</h4>
                    <p className="text-gray-300 text-sm">
                      Deploy your recruitment code across social networks and watch your empire expand.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-400/10 to-cyan-500/10 border border-blue-400/20 rounded-xl p-4 lg:p-6">
                    <div className="bg-gradient-to-r from-blue-400/20 to-cyan-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3 lg:mb-4">
                      <Star className="h-5 w-5 lg:h-6 lg:w-6 text-blue-400" />
                    </div>
                    <h4 className="text-base lg:text-lg font-bold text-white mb-2">Elite Activation</h4>
                    <p className="text-gray-300 text-sm">
                      When recruits join through your code, they become part of your exclusive network.
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-400/10 to-emerald-500/10 border border-green-400/20 rounded-xl p-4 lg:p-6">
                    <div className="bg-gradient-to-r from-green-400/20 to-emerald-500/20 rounded-lg p-2 lg:p-3 w-fit mb-3 lg:mb-4">
                      <Zap className="h-5 w-5 lg:h-6 lg:w-6 text-green-400" />
                    </div>
                    <h4 className="text-base lg:text-lg font-bold text-white mb-2">Profit Realization</h4>
                    <p className="text-gray-300 text-sm">
                      Earn continuous rewards from your network's activity and growth.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{
              background: snackbarSeverity == 'success' 
                ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                : 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#1f2937',
              fontWeight: 'bold'
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </WrapUpP2p>
  )
}